import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Loader2, Printer, QrCode } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = window.location.origin;
const qrImg = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=0&data=${encodeURIComponent(data)}`;

export default function MoldQrLabels() {
  const [molds, setMolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    axios.get(`${API}/molds`)
      .then(r => { setMolds(r.data); setLoading(false); })
      .catch(() => { toast.error("Erro"); setLoading(false); });
  }, []);

  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    if (Object.keys(selected).length === molds.length) setSelected({});
    else { const all = {}; molds.forEach(m => all[m.id] = true); setSelected(all); }
  };

  const chosen = molds.filter(m => selected[m.id]);

  if (loading) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="mold-qr-labels">
      <style>{`
        .print-area { display: none; }
        @media print {
          @page { size: A4; margin: 8mm; }
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area { display: grid !important; grid-template-columns: 1fr 1fr; gap: 5mm; position: absolute; left: 0; top: 0; width: 100%; }
          .qr-label { page-break-inside: avoid; break-inside: avoid; }
        }
        .qr-label {
          width: 96mm; height: 68mm; border: 1px dashed #94a3b8;
          background: #fff; padding: 4mm; display: flex; gap: 4mm;
          font-family: Arial, sans-serif; align-items: center; border-radius: 3mm;
        }
        .qr-label img { width: 60mm; height: 60mm; }
        .qr-label .info { flex: 1; }
        .qr-label .info h3 { font-size: 14pt; font-weight: 900; color: #0b1f3a; margin: 0; }
        .qr-label .info p { font-size: 9pt; color: #475569; margin: 1mm 0 0; }
        .qr-label .info .cav { font-size: 8pt; color: #64748b; margin-top: 2mm; }
        .qr-label .brand { position: absolute; bottom: 2mm; right: 3mm; font-size: 7pt; color: #f97316; font-weight: 700; }
      `}</style>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><QrCode className="h-8 w-8 text-blue-600" /> Crachás QR de Moldes</h1>
          <p className="text-sm text-muted-foreground mt-1">Imprime etiquetas A6 com QR — aponta o telemóvel para abrir a ficha completa.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={toggleAll} data-testid="mold-toggle-all">
            {Object.keys(selected).length === molds.length ? "Desmarcar" : "Selecionar todos"}
          </Button>
          <Button
            onClick={() => window.print()}
            disabled={chosen.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="print-mold-labels"
          >
            <Printer className="h-4 w-4 mr-2" /> Imprimir A4 ({chosen.length})
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {molds.map(m => {
          const url = `${APP_URL}/molds/${m.id}/full`;
          return (
            <label key={m.id} className={`p-3 border rounded-lg bg-white cursor-pointer flex items-center gap-3 ${selected[m.id] ? "border-blue-500 ring-1 ring-blue-500" : ""}`}>
              <input type="checkbox" checked={!!selected[m.id]} onChange={() => toggle(m.id)} data-testid={`mold-cb-${m.code}`} />
              <img src={qrImg(url)} alt="QR" className="w-16 h-16 rounded" />
              <div className="flex-1">
                <div className="font-bold">{m.code}</div>
                <div className="text-xs text-slate-600">{m.description}</div>
                <div className="text-[10px] text-slate-500">{m.cavities} cav. · ciclo {m.cycle_time}s</div>
              </div>
            </label>
          );
        })}
      </div>

      <div className="print-area">
        {chosen.map(m => {
          const url = `${APP_URL}/molds/${m.id}/full`;
          return (
            <div key={m.id} className="qr-label" style={{ position: "relative" }}>
              <img src={qrImg(url)} alt="QR" crossOrigin="anonymous" />
              <div className="info">
                <h3>{m.code}</h3>
                <p>{m.description}</p>
                <div className="cav">Cavidades: {m.cavities} · Ciclo: {m.cycle_time}s · Vida: {m.mold_life?.toLocaleString()}</div>
                <div className="cav">Ciclos: <b>{m.current_cycles?.toLocaleString() || 0}</b></div>
              </div>
              <div className="brand">EP3D</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
